// Copyright Epic Games, Inc. All Rights Reserved.

import { json } from "@remix-run/cloudflare";
import { useLoaderData } from "@remix-run/react";
import { parseCookie } from "cookie";

import { getSdk } from "~/generated/schema.graphcms";
import { mergeMeta } from "~/metatags/mergeMeta";
import { getGraphCms, getStage } from "~/services/graphcms";
import { isDownloadSection } from "~/typings";
import { getDictionary } from "~/utils/dictionaries";
import { countryElegibleForAltAppStore } from "~/utils/geo";
import { isValidUrl } from "~/utils/isValidUrl";
import { localeTokenMap } from "~/utils/localeTokenMap";

import { DownloadView } from "./DownloadView";

import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";

import type { GetPageQuery, Locale } from "~/generated/schema.graphcms";
import type { Locale as AppLocale } from "~/i18n.config";
import type { DownloadSection } from "~/typings";
import type { Dictionary } from "~/utils/dictionaries";

import type { MoreWaysToPlayLinks } from "./types";

export const meta: MetaFunction<typeof loader> = mergeMeta<typeof loader>(({ data }) => {
	const { page, url: requestUrl, locale } = data ?? {};

	const url = requestUrl ? new URL(requestUrl) : undefined;

	// MW-12222 will replace these inline strings with proper canonical/hreflang
	// emit. For now: clean download URL ("/download"), `og:lang` reflects the
	// resolved locale.
	const metaTags = [
		{ title: page?.title },
		{ property: "description", content: page?.description },
		{ property: "url", content: "/download" },

		{ property: "og:title", content: page?.title },
		{ property: "og:description", content: page?.description },
		{ property: "og:url", content: "/download" },
		{
			property: "og:site_name",
			content: "Rocket League Sideswipe Official Website",
		},
		{ property: "og:lang", content: locale },
		{ property: "og:type", content: "website" },

		{ property: "twitter:card", content: "summary_large_image" },
		{
			property: "twitter:title",
			content: page?.title,
		},
	];

	if (url) {
		metaTags.push({
			property: "og:image",
			content: `${url.origin}/images/RLS_KeyArt_Primary_Horiz_RGB_4K.jpg`,
		});
	} else {
		console.error("No request URL found for meta tags");
	}

	return metaTags;
});

export default function Download(): React.ReactNode {
	const { downloadSection, dict, isAltAppstoreEnabled, epicClientAndroidInstallerUrl } = useLoaderData<typeof loader>();

	if (!!downloadSection && isDownloadSection(downloadSection)) {
		const moreWaysToPlayLinks = getMoreWaysToPlayLinks(downloadSection.moreWaysToPlay);

		if (moreWaysToPlayLinks?.length === 0) {
			throw new Error('"More Ways To Play" download link list is empty.');
		}

		return (
			<DownloadView
				downloadSection={downloadSection}
				dict={dict}
				isAltAppstoreEnabled={isAltAppstoreEnabled}
				moreWaysToPlayLinks={moreWaysToPlayLinks}
				epicClientAndroidInstallerUrl={epicClientAndroidInstallerUrl}
			/>
		);
	} else {
		return <p>Nothing here</p>;
	}
}

export const loader = async ({ request, context }: LoaderFunctionArgs) => {
	const { locale } = context;
	const { env } = context.cloudflare;

	const mappedLocaleToken = localeTokenMap(locale);
	const page = await getPageData(env, mappedLocaleToken);
	const isAltAppstoreEnabled = countryElegibleForAltAppStore(request.headers);
	const dict = await getDictionary(locale);
	const cookies = parseCookie(request.headers.get("cookie") ?? "");

	const downloadSection = page?.sections?.[0];

	if (downloadSection === undefined || !isDownloadSection(downloadSection)) {
		throw new Error("Download section is not valid");
	}

	const trackingId = cookies["_epicSID"];
	const epicClientAndroidInstallerUrl = fallbackAndroidDownloadUrl(
		locale,
		downloadSection.downloadUrlAndroid ?? undefined,
		trackingId,
	);

	return json<{
		downloadSection: DownloadSection;
		dict: Dictionary;
		isAltAppstoreEnabled: boolean;
		epicClientAndroidInstallerUrl: string;
		page: GetPageQuery["page"];
		url: string;
		locale: AppLocale;
	}>({
		downloadSection,
		dict,
		isAltAppstoreEnabled,
		epicClientAndroidInstallerUrl,
		page,
		url: request.url,
		locale,
	});
};

const slug = "sideswipe-download";

async function getPageData(env: Env, mappedLocaleToken: string): Promise<GetPageQuery["page"] | undefined> {
	try {
		const { GetPage } = getSdk(getGraphCms(env));
		const pageData = await GetPage({
			locale: [mappedLocaleToken, "en"] as Locale[],
			slug,
			stage: getStage(env),
		});
		return pageData.page;
	} catch (err) {
		console.log(err);
		return undefined;
	}
}

function getMoreWaysToPlayLinks(moreWaysToPlayLinks: DownloadSection["moreWaysToPlay"]): MoreWaysToPlayLinks {
	return moreWaysToPlayLinks.reduce<{ href: string; title: string }[]>((acc, link) => {
		if (link.href && link.title) {
			acc.push({ href: link.href, title: link.title });
		} else {
			throw new Error(`"More Ways To Play" links have missing data: ${moreWaysToPlayLinks}`);
		}
		return acc;
	}, []);
}

/**
 * Generates a fallback Android download URL based on the provided parameters.
 *
 * If a URL is not provided, it generates a URL for the EGS Android download
 * page with the given locale and tracking ID. If a valid URL is provided
 * along with a tracking ID, it appends the `sid` parameter to the URL's query
 * string. Otherwise, it returns the provided URL as is.
 */
function fallbackAndroidDownloadUrl(locale: string, url?: string, trackingId?: string): string {
	if (!url) {
		return egsAndroidDownloadUrl(locale, trackingId);
	} else if (isValidUrl(url) && trackingId) {
		const externalUrl = new URL(url);
		externalUrl.searchParams.set("sid", trackingId);
		return externalUrl.toString();
	} else {
		return url;
	}
}

function egsAndroidDownloadUrl(locale: string, trackingId?: string): string {
	const url = new URL(`https://store.epicgames.com/${locale}/download/android`);
	url.searchParams.set("p", "rocket-league-sideswipe");

	if (trackingId) {
		url.searchParams.set("sid", trackingId);
	}

	return url.toString();
}
